
import { defineComponent, ref, onMounted, nextTick } from 'vue';

interface Message {
  id: string;
  text: string;
  user: boolean;
}

interface History {
  question: string;
  answer: string;
}

export default defineComponent({
  name: 'ChatBot',
  setup() {
    const dialogVisible = ref(false);
    const userInput = ref('');
    const messages = ref<Message[]>([]);
    const dialogContent = ref<HTMLTextAreaElement | null>(null);
    const historys = ref<History[]>([]);
    const isSending = ref(false);

    const questions = ref([
      {
        text: 'Hello, you can click on the following questions for consultation!',
        isTitle: true,
      },
      { text: 'About T-innoware Company' },
      { text: 'What are the AI capabilities of T-innoware?' },
      { text: 'Product Introduction of Network Packet Broker（NPB）' },
    ]);

    const chatDto = ref({
      kbIds: [
        {
          kbId: 'kb_3a3f665b7af4437da38f5c1672ebf9c2',
          dataIds: [{ dataId: 'd84fb85287a943c48e471308e57ad0d1' }],
          sourceType: 'unstructed',
        },
      ],
      sessionId: 'ld7af4ce92c44ef0992583b9fd31775e',
      questionId: 'someQuestionId', // 你需要根据实际情况替换 input.id
      qaConfig: {
        noAnswer: 'I cannot answer that question',
        topN: 5,
        cosineThreshold: 0.5,
        multiRoundEnable: true,
        roundNumber: 3,
        returnHits: true,
        faqKbId: 'k85b6a2eed194f569e69777ab387bb11',
        reasoning: false,
        searchInternet: false,
        maxTokens: 2048,
      },
      data: {
        inputText: '', // 你需要根据实际情况替换 input.text
      },
      history: historys,
      PromptParameters: {
        language: 'english',
      },
    });

    const toggleDialog = () => {
      dialogVisible.value = !dialogVisible.value;
      if (dialogVisible.value) {
        scrollToBottom();
      }
    };

    const sendMessage = () => {
      if (userInput.value.trim() !== '') {
        isSending.value = true;
        const newMessage = {
          id: generateRandomString(),
          text: userInput.value,
          user: true,
        };
        messages.value.push(newMessage);
        scrollToBottom();
        fetchEventStreamData(newMessage);
        // 清除输入框内容
        userInput.value = '';
      }
    };

    const handleLinkClick = (text: string) => {
      if (!isSending.value) {
        isSending.value = true;
        const newMessage = {
          id: generateRandomString(),
          text: text,
          user: true,
        };
        messages.value.push(newMessage);
        scrollToBottom();
        fetchEventStreamData(newMessage);
      }
    };

    const fetchEventStreamData = async (input: Message) => {
      const newMessage = { id: generateRandomString(), text: '', user: false };
      messages.value.push(newMessage);

      try {
        chatDto.value.data.inputText = input.text;
        chatDto.value.questionId = input.id;
        chatDto.value.history = historys.value.slice(-10);
        const response = await fetch('/algorithm/chat/search_chatV2', {
          method: 'POST',
          headers: {
            accept: 'text/event-stream',
            'Content-Type': 'application/json',
            connection: 'keep-alive',
          },
          body: JSON.stringify(chatDto.value),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        historys.value.push({ question: input.text, answer: '' });

        let buffer = ''; //起到数据缓冲的作用
        let text = '';
        let picture = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              if (text.length > 0) {
                historys.value[historys.value.length - 1].answer = text;
              }
              isSending.value = false;
              break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = JSON.parse(line.slice(6));
                if (data.errcode === 0 && !data.stop) {
                  const index = messages.value.findIndex(
                    (msg) => msg.id === newMessage.id
                  );
                  if (index !== -1) {
                    if(data.data){
                      if (data.data.contentType === 'text') {
                        text += data.data.content;
                        messages.value[index].text = text;
                        await new Promise((resolve) => setTimeout(resolve, 50));
                      } else if (data.data.contentType === 'picture') {
                        picture += `<br/><img src="data:image/png;base64,${data.data.content}" />`;
                        messages.value[index].text += picture;
                      }
                      scrollToBottom();
                    }
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Fetch error:', error);
        isSending.value = false;
        const index = messages.value.findIndex(
          (msg) => msg.id === newMessage.id
        );
        if (index !== -1) {
          messages.value[index].text = 'Sorry!';
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/shift + Enter 换行
      if (event.key === 'Enter') {
        if (event.ctrlKey) {
          userInput.value += '\n';
        } else if (event.shiftKey) {
          return;
        } else {
          if (!isSending.value) {
            // Enter 发送消息
            event.preventDefault();
            sendMessage();
          } else {
            return;
          }
        }
      }
    };

    const generateRandomString = () => {
      const chars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 32; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    };

    const scrollToBottom = () => {
      nextTick(() => {
        dialogContent.value?.scrollTo(0, dialogContent.value.scrollHeight);
      });
    };

    onMounted(() => {
      if (dialogVisible.value) {
        scrollToBottom();
      }
    });

    return {
      dialogVisible,
      userInput,
      messages,
      questions,
      dialogContent,
      isSending,
      toggleDialog,
      sendMessage,
      handleLinkClick,
      fetchEventStreamData,
      handleKeyDown,
      generateRandomString,
      scrollToBottom,
    };
  },
});
